import {
  CURRENT_EULA_EFFECTIVE_DATE_DISPLAY,
  CURRENT_EULA_LAST_UPDATED_DISPLAY,
  CURRENT_EULA_VERSION,
  VIRRO_LEGAL_EMAIL,
  VIRRO_LEGAL_EMAIL_HREF,
  VIRRO_SYSTEM_ADMIN_EMAIL,
  VIRRO_SYSTEM_ADMIN_EMAIL_HREF,
} from '@/lib/eula';

/**
 * The full Atlas EULA body — everything an Authorized User must read before
 * clicking "I Agree." Rendered inside both the public legal page
 * (src/pages/legal/EulaPage.tsx) and the authenticated click-through gate
 * (src/pages/legal/EulaAcceptPage.tsx).
 *
 * To preserve audit traceability across versions, never edit the substantive
 * text here without ALSO bumping CURRENT_EULA_VERSION + CURRENT_EULA_EFFECTIVE_DATE
 * in src/lib/eula.ts. The acceptance log records the version string the user
 * was shown; an auditor must be able to git-show that string and see the
 * exact prose it referenced.
 */
export function EulaDocument() {
  return (
    <>
      <div className="space-y-3 border-b border-border pb-8">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Legal · {CURRENT_EULA_VERSION}
        </p>
        <h1 className="text-4xl font-medium tracking-headline leading-headline">
          Virro Atlas — End-User License Agreement
        </h1>
        <dl className="grid grid-cols-2 gap-2 pt-2 text-sm sm:max-w-md">
          <div>
            <dt className="font-medium text-foreground">Effective Date</dt>
            <dd className="text-muted-foreground">
              {CURRENT_EULA_EFFECTIVE_DATE_DISPLAY}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-foreground">Last Updated</dt>
            <dd className="text-muted-foreground">
              {CURRENT_EULA_LAST_UPDATED_DISPLAY}
            </dd>
          </div>
        </dl>
      </div>

      <Notice>
        <p>
          <strong>IMPORTANT — PLEASE READ CAREFULLY.</strong> This End-User
          License Agreement (&ldquo;<strong>Agreement</strong>&rdquo; or
          &ldquo;<strong>EULA</strong>&rdquo;) is a binding legal contract
          between you (&ldquo;<strong>you</strong>&rdquo; or &ldquo;
          <strong>User</strong>&rdquo;) and Eloo LLC d/b/a Virro Life
          (&ldquo;<strong>Virro</strong>,&rdquo; &ldquo;<strong>Company</strong>
          ,&rdquo; &ldquo;<strong>we</strong>,&rdquo; &ldquo;<strong>us</strong>
          ,&rdquo; or &ldquo;<strong>our</strong>&rdquo;) governing your access
          to and use of <strong>Virro Atlas</strong>, the Company&rsquo;s
          internal enterprise resource planning (&ldquo;<strong>ERP</strong>
          &rdquo;) system, including all associated software, modules,
          dashboards, databases, APIs, scripts, AI features, embedded models,
          internal documentation, source code, and any related tools or
          materials (collectively, the &ldquo;<strong>System</strong>&rdquo;).
        </p>
        <p>
          <strong>
            Virro Atlas is proprietary, confidential, internal-use software. It
            is not a public product. Access is granted on a strictly limited,
            revocable basis solely to authorized personnel of the Company.
          </strong>
        </p>
        <p>
          <strong>
            By logging in to, accessing, contributing to, or otherwise using
            the System, you agree to this EULA.
          </strong>{' '}
          If you do not agree, you must not access the System and must
          immediately notify your manager or the System Administrator.
        </p>
      </Notice>

      <Section number="1" title="Definitions">
        <ul className="list-disc space-y-3 pl-5">
          <li>
            <strong>&ldquo;Authorized User&rdquo;</strong> means an individual
            whom the Company has expressly authorized to access the System,
            including (a) Company employees, (b) Company officers, members, and
            managers, and (c) contractors, consultants, advisors, vendors, and
            service providers engaged by the Company who have signed or
            otherwise accepted this EULA and any required confidentiality
            agreement.
          </li>
          <li>
            <strong>&ldquo;Company Data&rdquo;</strong> means all data, records,
            content, configurations, and outputs stored, processed, displayed,
            generated, or transmitted by or through the System, including
            without limitation customer and prospect records, sales data,
            financial records, accounting entries, bills of materials, work
            orders, inventory ledgers, supplier records, product specifications,
            manufacturing process data, pricing, margins, forecasts, marketing
            plans, AI-generated outputs, logs, and any data derived from the
            foregoing.
          </li>
          <li>
            <strong>&ldquo;Confidential Information&rdquo;</strong> has the
            meaning given in Section 7.
          </li>
          <li>
            <strong>&ldquo;Contributions&rdquo;</strong> means any code,
            configuration, schema, prompt, script, design, documentation, data,
            comment, issue, proposal, or other material that you create,
            modify, submit, or commit to the System or to any Company
            repository, environment, or workspace associated with the System
            (including any &ldquo;vibe-coded,&rdquo; AI-assisted, or
            pair-programmed material).
          </li>
          <li>
            <strong>&ldquo;Credentials&rdquo;</strong> means any usernames,
            passwords, API keys, personal access tokens, SSH keys, OAuth
            tokens, multi-factor authentication devices, session cookies, or
            other authentication artifacts associated with the System.
          </li>
          <li>
            <strong>&ldquo;System Administrator&rdquo;</strong> means the
            individual(s) designated by the Company from time to time to
            administer access, security, and configuration of the System.
          </li>
        </ul>
      </Section>

      <Section number="2" title="Nature of the System">
        <p>
          Virro Atlas is the Company&rsquo;s internal ERP and operations
          platform, presently encompassing (without limitation) Sales/CRM,
          Financials, and Manufacturing modules and may, at the Company&rsquo;s
          discretion, be expanded to cover additional areas of Company
          operations. The System is provided to Authorized Users{' '}
          <strong>solely as a tool to perform work for the Company</strong>.
          The System is under continuous development; features may be added,
          modified, removed, or made unavailable at any time without notice.
          Nothing in the System is offered to the public, and no representation
          is made that the System is fit for any external purpose.
        </p>
      </Section>

      <Section number="3" title="License Grant">
        <p>
          Subject to this EULA and your continued status as an Authorized User
          in good standing, the Company grants you a{' '}
          <strong>
            limited, revocable, non-exclusive, non-transferable,
            non-sublicensable, royalty-free
          </strong>{' '}
          license to access and use the System solely to perform your
          authorized duties for the Company.{' '}
          <strong>All rights not expressly granted are reserved.</strong> No
          license is granted by implication, estoppel, or otherwise.
        </p>
      </Section>

      <Section number="4" title="Authorized Users and Credentials">
        <Subsection title="4.1 Personal Use Only">
          <p>
            Your access is personal to you. You may not share, transfer, lend,
            sell, or disclose your Credentials to any other person — including
            coworkers, family members, or other Authorized Users — and you may
            not permit any other person to access the System using your
            Credentials, account, or session.
          </p>
        </Subsection>
        <Subsection title="4.2 Credential Hygiene">
          <p>
            You agree to (a) keep all Credentials secret and secure; (b) use
            unique, strong passwords and enable any required multi-factor
            authentication; (c) not store Credentials in plaintext in unsecured
            locations (including chat messages, code comments, public or
            private repositories outside Company-approved secret stores,
            screenshots, or personal notes); and (d) immediately notify the
            System Administrator of any actual or suspected loss, theft,
            compromise, or unauthorized use of your Credentials or any
            unauthorized access to the System.
          </p>
        </Subsection>
        <Subsection title="4.3 Devices and Environments">
          <p>
            You may access the System only from devices and network
            environments that meet any security requirements specified by the
            Company from time to time (including disk encryption, current
            operating system, screen lock, and approved antivirus or endpoint
            software where required). You may not access the System from public
            or shared computers, jailbroken or rooted devices, or untrusted
            networks where doing so would create a foreseeable risk of
            credential or data compromise.
          </p>
        </Subsection>
      </Section>

      <Section number="5" title="Acceptable Use">
        <p>
          You agree to use the System only as authorized by the Company and
          only for legitimate Company business purposes. You agree{' '}
          <strong>not</strong> to:
        </p>
        <Lettered
          items={[
            <>
              access, view, copy, export, modify, or delete any Company Data
              that is outside the scope of your role or your assigned tasks;
            </>,
            <>
              use the System for any personal, side-business, competitive, or
              non-Company purpose;
            </>,
            <>
              export, download, copy, screenshot, photograph, transmit, email,
              message, paste, upload, or otherwise remove Company Data from the
              System or from Company-controlled environments, except as
              expressly required by your role and using Company-approved
              methods;
            </>,
            <>
              connect the System to, or transmit Company Data to, any
              third-party tool, AI service, large-language-model interface,
              browser extension, automation platform, or other external system
              without express written authorization from the System
              Administrator;
            </>,
            <>
              reverse engineer, decompile, disassemble, or attempt to derive
              the source code, architecture, model weights, system prompts,
              schemas, or trade secrets of the System (other than source code
              lawfully made available to you in your role), except to the
              extent expressly permitted by applicable law that cannot be
              waived;
            </>,
            <>
              circumvent, disable, or interfere with any authentication,
              authorization, rate-limiting, logging, monitoring, or
              access-control feature of the System, or attempt to elevate your
              privileges beyond those granted;
            </>,
            <>
              introduce any virus, worm, trojan, ransomware, malicious code, or
              unauthorized software into the System or into any Company
              environment;
            </>,
            <>
              modify, deface, vandalize, or knowingly corrupt Company Data,
              configurations, schemas, or production environments;
            </>,
            <>
              use the System to harass, defame, surveil, or unlawfully
              discriminate against any person;
            </>,
            <>
              violate any law, regulation, contract, or third-party right
              (including privacy, intellectual property, or export-control
              laws) in connection with your use of the System;
            </>,
            <>
              probe, scan, penetration-test, fuzz, or red-team the System
              without prior written authorization from the System Administrator
              (this restriction does not limit normal development and QA
              activity within your assigned role); or
            </>,
            <>
              assist, encourage, or enable any other person to do any of the
              foregoing.
            </>,
          ]}
        />
      </Section>

      <Section number="6" title="Restrictions on Copying and Removal">
        <p>Without limiting Section 5, you specifically agree that:</p>
        <Lettered
          items={[
            <>
              you will <strong>not</strong> create, retain, or maintain any
              copy, mirror, fork, replica, backup, export, or extract of the
              System or any non-trivial portion of Company Data on any personal
              device, personal account, personal cloud storage, personal email,
              personal repository, personal AI tool history, or other resource
              not owned or controlled by the Company;
            </>,
            <>
              any local clone, working copy, cache, snapshot, or development
              branch you create in the course of your work is the property of
              the Company, must reside in Company-approved environments where
              reasonably possible, and must be returned or destroyed in
              accordance with Section 17;
            </>,
            <>
              you will not use generative AI tools, code assistants, or
              third-party APIs in a manner that transmits Company Data, source
              code, schemas, or Confidential Information to providers other
              than those approved in writing by the Company; and
            </>,
            <>
              you will not pool, aggregate, or combine Company Data with
              personal datasets or with data of any other employer, client, or
              project.
            </>,
          ]}
        />
      </Section>

      <Section number="7" title="Confidentiality and Trade Secrets">
        <Subsection title="7.1 Acknowledgment">
          <p>
            You acknowledge that the System and Company Data contain valuable
            trade secrets and Confidential Information of the Company, the
            unauthorized disclosure of which would cause immediate,
            substantial, and irreparable harm to the Company.{' '}
            <strong>&ldquo;Confidential Information&rdquo;</strong> means all
            non-public information about the Company or its business that is
            disclosed to you, observed by you, or accessed by you through the
            System, in any form, including without limitation: source code,
            architecture, schemas, prompts, model configurations, AI-generated
            content, customer and prospect lists, supplier lists, pricing,
            margins, costs, sales pipelines, financial records, forecasts,
            business plans, marketing strategies, product designs, product
            roadmaps, manufacturing processes, BOMs, work orders, inventory
            data, vendor terms, employee compensation, legal matters, strategic
            communications, and any information marked or that a reasonable
            person would understand to be confidential.
          </p>
        </Subsection>
        <Subsection title="7.2 Obligations">
          <p>
            You agree to (a) hold all Confidential Information in strict
            confidence; (b) use Confidential Information only as necessary to
            perform your authorized duties for the Company; (c) not disclose
            Confidential Information to any person inside or outside the
            Company who does not have a clear need-to-know and is not bound by
            equivalent confidentiality obligations; (d) protect Confidential
            Information using at least the same degree of care you would use to
            protect your own most sensitive information, and in no event less
            than reasonable care; and (e) immediately notify the Company of any
            unauthorized use, disclosure, loss, or compromise of Confidential
            Information.
          </p>
        </Subsection>
        <Subsection title="7.3 Exclusions">
          <p>
            Confidential Information does not include information that (a) is
            or becomes publicly known through no fault of yours; (b) was
            lawfully in your possession, free of any confidentiality
            obligation, before your access to the System; or (c) is
            independently developed by you without use of or reference to
            Confidential Information. Information does not lose protection
            merely because it can be reconstructed from public sources with
            effort.
          </p>
        </Subsection>
        <Subsection title="7.4 Compelled Disclosure">
          <p>
            If you are legally compelled to disclose Confidential Information,
            you will, to the extent permitted by law, give the Company prompt
            written notice and reasonable cooperation so that the Company may
            seek a protective order or other appropriate remedy.
          </p>
        </Subsection>
        <Subsection title="7.5 Survival">
          <p>
            Your obligations under this Section 7 survive termination of your
            access to the System and your engagement with the Company{' '}
            <strong>indefinitely</strong> with respect to trade secrets, and
            for at least <strong>five (5) years</strong> after termination with
            respect to other Confidential Information, or longer if required by
            applicable law or any separate confidentiality agreement.
          </p>
        </Subsection>
      </Section>

      <Section number="8" title="Company Data; No Personal Data Rights">
        <p>
          All Company Data is and shall remain the sole and exclusive property
          of the Company. You acquire no right, title, or interest in any
          Company Data by virtue of accessing or using the System.{' '}
          <strong>
            Personal data of customers, employees, prospects, vendors, or any
            other individual that is processed through the System belongs to
            the Company (or to the data subject, as applicable) and may not be
            used by you for any personal purpose, marketing, profiling, or any
            purpose unrelated to your authorized duties.
          </strong>
        </p>
      </Section>

      <Section
        number="9"
        title="Contributions and Intellectual Property Assignment"
      >
        <Subsection title="9.1 Work Made for Hire">
          <p>
            To the maximum extent permitted by law, all Contributions you make
            to the System are &ldquo;works made for hire&rdquo; within the
            meaning of the U.S. Copyright Act and are owned exclusively by the
            Company from the moment of creation.
          </p>
        </Subsection>
        <Subsection title="9.2 Assignment">
          <p>
            To the extent any Contribution is not deemed a work made for hire,{' '}
            <strong>
              you hereby irrevocably assign and transfer to the Company all
              right, title, and interest in and to such Contribution worldwide,
              including all copyrights, patent rights, trade-secret rights,
              moral rights, and other intellectual property rights, and all
              rights to sue for past, present, and future infringement.
            </strong>{' '}
            You waive any moral rights to the maximum extent permitted by law.
          </p>
        </Subsection>
        <Subsection title="9.3 AI-Assisted Contributions">
          <p>
            This Section 9 applies in full to Contributions you generate,
            draft, or produce with the assistance of AI coding tools, code
            assistants, large language models, or any other automated means
            (&ldquo;<strong>AI-Assisted Contributions</strong>&rdquo;). You
            represent that all AI-Assisted Contributions you submit (a) were
            produced using AI tools approved or permitted by the Company, (b)
            do not, to your knowledge, infringe any third-party intellectual
            property right, and (c) were reviewed by you for accuracy,
            security, and licensing compliance before submission.
          </p>
        </Subsection>
        <Subsection title="9.4 Pre-Existing Materials">
          <p>
            You may not incorporate into the System any code, content, or
            materials owned by you or by any third party (including prior
            employers, open-source projects, or AI training corpora) unless (a)
            you have all necessary rights to do so, (b) you have disclosed the
            incorporation in writing to the Company, and (c) the licensing
            terms are compatible with the Company&rsquo;s use and approved by
            the Company.
          </p>
        </Subsection>
        <Subsection title="9.5 Further Assurances">
          <p>
            You agree to execute any documents and take any further actions
            reasonably requested by the Company to perfect, register, or
            enforce the Company&rsquo;s rights under this Section 9, at the
            Company&rsquo;s expense. You appoint the Company as your
            attorney-in-fact, with full authority, to execute such documents on
            your behalf if you fail to do so within a reasonable time after
            request.
          </p>
        </Subsection>
        <Subsection title="9.6 Feedback">
          <p>
            Suggestions, ideas, feature requests, and feedback you provide
            regarding the System are owned by the Company and may be used by
            the Company for any purpose without restriction or compensation to
            you.
          </p>
        </Subsection>
      </Section>

      <Section number="10" title="AI Features">
        <p>
          The System includes or may include features powered by artificial
          intelligence, machine learning, large language models, or third-party
          AI services (collectively, &ldquo;<strong>AI Features</strong>
          &rdquo;). The following terms apply to your use of AI Features and
          are in addition to Section 5:
        </p>
        <Subsection title="10.1 Permitted Use">
          <p>
            You may use AI Features only for authorized Company business
            purposes, only with prompts and inputs that are appropriate for the
            System, and only in accordance with any usage guidelines published
            by the Company.
          </p>
        </Subsection>
        <Subsection title="10.2 No Sensitive Inputs to External Models">
          <p>
            You may not submit Confidential Information, customer personal
            data, regulated data (including health, financial, or
            government-regulated data), source code, or other sensitive Company
            Data to any AI tool, model, or service that has not been expressly
            approved in writing for that purpose by the Company. Approved tools
            and the categories of data permitted in each tool will be
            maintained by the System Administrator.
          </p>
        </Subsection>
        <Subsection title="10.3 Cost and Abuse">
          <p>
            AI Features have measurable per-use costs. You may not (a) issue
            requests in volumes, sizes, or patterns reasonably likely to cause
            excessive third-party API costs, (b) use AI Features for
            non-Company purposes, or (c) attempt to exfiltrate model prompts,
            weights, or proprietary configurations.
          </p>
        </Subsection>
        <Subsection title="10.4 No Reliance">
          <p>
            AI Features may produce inaccurate, incomplete, biased, or
            fabricated outputs (&ldquo;<strong>hallucinations</strong>
            &rdquo;). You are solely responsible for evaluating and verifying
            any AI output before relying on it for a Company decision.{' '}
            <strong>
              AI outputs are not a substitute for professional judgment
            </strong>
            , and you may not use AI outputs as the sole basis for any decision
            with legal, financial, regulatory, employment, safety, or
            compliance consequences.
          </p>
        </Subsection>
        <Subsection title="10.5 Logging">
          <p>
            The Company may log, retain, and review prompts, inputs, outputs,
            and metadata associated with AI Features for purposes including
            security, debugging, audit, training, evaluation, and enforcement
            of this EULA.
          </p>
        </Subsection>
      </Section>

      <Section number="11" title="Monitoring; No Expectation of Privacy">
        <p>
          You acknowledge and agree that the System and all activity within it
          are the property of the Company and may be{' '}
          <strong>
            monitored, logged, recorded, audited, and reviewed by the Company
            at any time, with or without notice
          </strong>
          , to the maximum extent permitted by law. This includes login events,
          queries, edits, exports, API calls, AI prompts and outputs, search
          history, comments, messages, code commits, file accesses, and any
          other interaction with the System.{' '}
          <strong>
            You have no expectation of privacy in any activity performed within
            or in connection with the System
          </strong>
          , including any personal communications you choose to conduct through
          it (which you should not). The Company may use logs and monitoring
          data for any lawful business purpose, including security, compliance,
          performance, training, and enforcement of this EULA.
        </p>
      </Section>

      <Section number="12" title="Security Obligations">
        <p>
          You agree to (a) promptly install required security updates on
          devices used to access the System; (b) report any suspected security
          incident, vulnerability, phishing attempt, or anomalous behavior to
          the System Administrator without delay; (c) cooperate with any
          Company investigation into suspected misuse, breach, or policy
          violation; and (d) comply with any additional security policies,
          standards, or controls adopted by the Company from time to time.
        </p>
      </Section>

      <Section number="13" title="Third-Party Components and Open Source">
        <p>
          The System may incorporate third-party software, services, libraries,
          or open-source components (&ldquo;
          <strong>Third-Party Components</strong>&rdquo;), each of which is
          subject to its own license terms. Nothing in this EULA limits your
          rights or obligations under any open-source license that applies to a
          Third-Party Component. The Company makes no representation or
          warranty regarding any Third-Party Component, and your use of any
          Third-Party Component within the System must comply with its license.
        </p>
      </Section>

      <Section number="14" title="No Warranty">
        <Notice>
          <p className="uppercase">
            <strong>
              The System is provided &ldquo;as is&rdquo; and &ldquo;as
              available,&rdquo; with all faults, for internal use only.
            </strong>{' '}
            To the maximum extent permitted by law, the Company disclaims all
            warranties of any kind, express, implied, or statutory, including
            warranties of merchantability, fitness for a particular purpose,
            non-infringement, accuracy, reliability, availability, and title.
            The Company does not warrant that the System will be uninterrupted,
            error-free, secure, or free of data loss, and you acknowledge that
            the System is under active development and may contain bugs,
            incomplete features, or incorrect outputs.
          </p>
        </Notice>
      </Section>

      <Section number="15" title="Limitation of Liability">
        <Notice>
          <p className="uppercase">
            <strong>
              To the maximum extent permitted by law, the Company will not be
              liable to you for any indirect, incidental, special,
              consequential, exemplary, or punitive damages, or for lost
              profits, revenue, goodwill, or data, arising out of or related to
              your use of the System or this EULA, whether based on contract,
              tort, negligence, strict liability, or any other legal theory,
              even if advised of the possibility of such damages.
            </strong>
          </p>
          <p>
            Nothing in this Section limits any obligation you owe the Company
            under any employment, contractor, confidentiality, or
            intellectual-property assignment agreement, or under Sections 7
            (Confidentiality), 9 (IP Assignment), or 16 (Remedies).
          </p>
        </Notice>
      </Section>

      <Section number="16" title="Remedies; Injunctive Relief">
        <p>
          You acknowledge that any breach of Sections 5, 6, 7, 8, 9, 10, or 11
          would cause the Company immediate and irreparable harm for which
          monetary damages would be inadequate.{' '}
          <strong>
            The Company is therefore entitled to seek injunctive and other
            equitable relief, without bond and without the need to prove actual
            damages, in addition to any other remedies available at law or in
            equity.
          </strong>{' '}
          The Company&rsquo;s remedies under this EULA are cumulative.
        </p>
      </Section>

      <Section number="17" title="Term and Termination">
        <Subsection title="17.1 Term">
          <p>
            This EULA takes effect on the earlier of (a) your first access to
            the System and (b) your acceptance of this EULA, and continues
            until terminated as set forth below.
          </p>
        </Subsection>
        <Subsection title="17.2 Termination by the Company">
          <p>
            The Company may suspend or terminate your access to the System and
            this EULA, in whole or in part,{' '}
            <strong>
              at any time and for any or no reason, with or without notice
            </strong>
            , including upon (a) termination or change of your employment or
            engagement with the Company; (b) any actual or suspected violation
            of this EULA, any Company policy, or any law; (c) any actual or
            suspected security or confidentiality concern; or (d) any change in
            your role that no longer requires System access.
          </p>
        </Subsection>
        <Subsection title="17.3 Automatic Termination">
          <p>
            Your license under Section 3 terminates{' '}
            <strong>automatically and immediately</strong> upon the end of your
            employment or engagement with the Company, without any further
            action by either party.
          </p>
        </Subsection>
        <Subsection title="17.4 Effect of Termination">
          <p>
            Upon termination, you must immediately (a) cease all use of the
            System; (b) return to the Company, or, at the Company&rsquo;s
            election, securely destroy, all copies of Company Data,
            Confidential Information, and Company materials in your possession
            or control, in any medium; (c) delete or surrender all local
            clones, working copies, exports, AI prompt histories, and chat or
            tool logs containing Company Data; (d) provide written
            certification of compliance with this Section if requested by the
            Company; and (e) cooperate with any reasonable transition or
            knowledge-transfer requested by the Company. Sections 6, 7, 8, 9,
            11, 14, 15, 16, 17.4, 18, and 19 survive termination.
          </p>
        </Subsection>
      </Section>

      <Section number="18" title="Governing Law; Disputes">
        <p>
          This EULA is governed by the laws of the State of{' '}
          <strong>Ohio</strong>, without regard to its conflict-of-laws
          principles, and applicable U.S. federal law. Any dispute arising out
          of or relating to this EULA that is not resolved informally will be
          brought exclusively in the state or federal courts located in{' '}
          <strong>Hancock County, Ohio</strong>, and you consent to the
          personal jurisdiction and venue of those courts.{' '}
          <strong>
            Notwithstanding the foregoing, the Company may seek injunctive or
            equitable relief in any court of competent jurisdiction to protect
            its Confidential Information, intellectual property, or System
            security.
          </strong>{' '}
          The prevailing party in any action to enforce this EULA is entitled
          to recover its reasonable attorneys&rsquo; fees and costs to the
          maximum extent permitted by law.
        </p>
      </Section>

      <Section number="19" title="Miscellaneous">
        <ul className="list-disc space-y-3 pl-5">
          <li>
            <strong>Relationship to Other Agreements.</strong> This EULA
            supplements, and does not replace, any employment agreement,
            contractor agreement, confidentiality agreement, IP assignment
            agreement, or Company policy applicable to you. In the event of a
            conflict, the agreement or policy that provides the greatest
            protection to the Company controls.
          </li>
          <li>
            <strong>Entire Agreement.</strong> Together with the agreements and
            policies referenced above, this EULA is the entire agreement
            between you and the Company regarding the System and supersedes all
            prior understandings on the subject.
          </li>
          <li>
            <strong>Amendments.</strong> The Company may modify this EULA at
            any time by posting an updated version within the System or by
            other reasonable notice. Your continued use of the System after the
            effective date of any update constitutes acceptance.
          </li>
          <li>
            <strong>Severability.</strong> If any provision is held
            unenforceable, the remainder remains in full force, and the
            unenforceable provision will be modified to the minimum extent
            necessary to make it enforceable.
          </li>
          <li>
            <strong>No Waiver.</strong> No failure or delay by the Company in
            exercising any right under this EULA operates as a waiver of that
            right.
          </li>
          <li>
            <strong>Assignment.</strong> You may not assign this EULA. The
            Company may assign this EULA freely, including in connection with a
            merger, acquisition, reorganization, or sale of assets.
          </li>
          <li>
            <strong>No Employment Promise.</strong> Nothing in this EULA
            creates any promise of continued employment or engagement, and your
            access to the System does not entitle you to any compensation,
            equity, or benefit beyond what is set forth in your separate
            written agreement with the Company.
          </li>
          <li>
            <strong>Notices.</strong> The Company may give you notice through
            the System, by email, or by other reasonable means. You may give
            notice to the Company at the address below.
          </li>
          <li>
            <strong>Headings.</strong> Section headings are for convenience
            only and do not affect interpretation.
          </li>
        </ul>
      </Section>

      <Section number="20" title="Contact">
        <address className="not-italic">
          <p className="font-medium text-foreground">
            Eloo LLC d/b/a Virro Life
          </p>
          <p>230 Stanford Parkway</p>
          <p>Findlay, OH 45840</p>
          <p className="mt-3">
            Legal:{' '}
            <a
              href={`mailto:${VIRRO_LEGAL_EMAIL_HREF}`}
              className="text-atmosphere-dark underline-offset-4 hover:underline"
            >
              {VIRRO_LEGAL_EMAIL}
            </a>
          </p>
          <p>
            System Administrator:{' '}
            <a
              href={`mailto:${VIRRO_SYSTEM_ADMIN_EMAIL_HREF}`}
              className="text-atmosphere-dark underline-offset-4 hover:underline"
            >
              {VIRRO_SYSTEM_ADMIN_EMAIL}
            </a>
          </p>
        </address>
      </Section>

      <Section number="21" title="Acknowledgment and Acceptance">
        <p>
          By clicking &ldquo;I Agree&rdquo; below, you acknowledge and agree
          that:
        </p>
        <ul className="list-disc space-y-3 pl-5">
          <li>
            You have read this EULA in full and have had a reasonable
            opportunity to review it and ask questions before accepting;
          </li>
          <li>
            You understand that this EULA is a binding legal contract between
            you and Eloo LLC d/b/a Virro Life;
          </li>
          <li>
            You are an Authorized User and are accepting this EULA in your
            individual capacity;
          </li>
          <li>
            You agree to be bound by all of the terms of this EULA, including
            without limitation the obligations regarding Confidentiality (§7),
            Intellectual Property Assignment (§9), Monitoring and No
            Expectation of Privacy (§11), and Termination (§17);
          </li>
          <li>
            Your click-through acceptance has the same legal force and effect
            as a handwritten signature under the U.S. Electronic Signatures in
            Global and National Commerce Act (E-SIGN), the Uniform Electronic
            Transactions Act (UETA), and any applicable state or foreign
            equivalent; and
          </li>
          <li>
            The Company may record and retain your username, IP address,
            device information, EULA version, and timestamp as evidence of
            your acceptance.
          </li>
        </ul>
        <p>
          If you do not agree to this EULA, click &ldquo;Decline&rdquo; or
          close this window. You will not be granted access to the System.
        </p>
      </Section>
    </>
  );
}

function Section({
  number,
  title,
  children,
}: {
  number: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-12 space-y-4 scroll-mt-24" id={`section-${number}`}>
      <h2 className="text-xl font-medium tracking-headline leading-headline">
        <span className="mr-2 text-muted-foreground">{number}.</span>
        {title}
      </h2>
      <div className="space-y-4 text-sm">{children}</div>
    </section>
  );
}

function Subsection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-foreground">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <aside
      role="note"
      className="my-6 space-y-3 rounded-lg border-l-4 border-foreground bg-secondary/60 px-5 py-4 text-sm"
    >
      {children}
    </aside>
  );
}

function Lettered({ items }: { items: React.ReactNode[] }) {
  return (
    <ol className="space-y-3 pl-1 text-sm">
      {items.map((node, idx) => (
        <li key={idx} className="flex gap-2">
          <span aria-hidden className="shrink-0 tabular-nums text-muted-foreground">
            ({String.fromCharCode(97 + idx)})
          </span>
          <div className="flex-1">{node}</div>
        </li>
      ))}
    </ol>
  );
}
